import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { WRITE_ROLES } from '@familienbaum/shared';
import type {
  ActivityListQuery,
  AccessRequestSummary,
  ArchiveInfo,
  BranchInput,
  BranchSuggestion,
  BranchSummary,
  ScopePreview,
  ArchiveInput,
  AnswerInput,
  AuditEntry,
  DashboardResponse,
  ArchiveFaceCandidates,
  GapItem,
  GapSummary,
  ImportApplyInput,
  ImportBulk,
  ImportDecisionPatch,
  ImportDetail,
  ImportEdgeItem,
  ImportPersonItem,
  ImportPlaceItem,
  ImportSummary,
  EventInput,
  DetectedRegionsInput,
  EventSummary,
  FaceCluster,
  PersonFaceCandidates,
  FamilyMember,
  InviteSummary,
  MediaPatch,
  MediaRegion,
  MediaRegionInput,
  MediaRegionPatch,
  MediaSummary,
  MediaUploadResult,
  AgeComparisonInput,
  AgeComparisonTasks,
  Paginated,
  PersonDetail,
  PersonInput,
  PersonPatch,
  PersonRef,
  PersonSummary,
  PlaceInput,
  PlaceSummary,
  QuestionCounts,
  QuestionDetail,
  QuestionInput,
  QuestionSummary,
  RelationPathResponse,
  RelationshipInput,
  RelationshipSummary,
  SearchResponse,
  SessionResponse,
  SourceInput,
  SourceLinkSummary,
  SourceSummary,
  TreeResponse,
  UpdateProfileInput,
  UpdateUserInput,
  UserSummary,
} from '@familienbaum/shared';
import { api } from './api';
import { embedRegions } from './faces/backfill';
import { toQueryString } from './utils';

export const keys = {
  session: ['session'] as const,
  authStatus: ['auth', 'status'] as const,
  dashboard: ['dashboard'] as const,
  persons: (params: Record<string, unknown>) => ['persons', params] as const,
  person: (id: string) => ['person', id] as const,
  personAudit: (id: string) => ['personAudit', id] as const,
  activity: (params: Record<string, unknown>) => ['activity', params] as const,
  relationships: (params: Record<string, unknown>) => ['relationships', params] as const,
  relationship: (id: string) => ['relationship', id] as const,
  events: (params: Record<string, unknown>) => ['events', params] as const,
  event: (id: string) => ['event', id] as const,
  places: (params: Record<string, unknown>) => ['places', params] as const,
  place: (id: string) => ['place', id] as const,
  media: (params: Record<string, unknown>) => ['media', params] as const,
  ageComparisons: (params: Record<string, unknown>) => ['ageComparisons', params] as const,
  mediaItem: (id: string) => ['mediaItem', id] as const,
  sources: (params: Record<string, unknown>) => ['sources', params] as const,
  source: (id: string) => ['source', id] as const,
  sourceLinks: (id: string) => ['sourceLinks', id] as const,
  tree: (params: Record<string, unknown>) => ['tree', params] as const,
  relationPath: (from: string, to: string) => ['relationPath', from, to] as const,
  users: ['users'] as const,
  familyMembers: (params: Record<string, unknown> = {}) => ['familyMembers', params] as const,
  invites: ['invites'] as const,
  search: (q: string) => ['search', q] as const,
  questions: (params: Record<string, unknown>) => ['questions', params] as const,
  question: (id: string) => ['question', id] as const,
  questionCounts: ['questionCounts'] as const,
  gaps: (params: Record<string, unknown>) => ['gaps', params] as const,
  gapSummary: ['gapSummary'] as const,
  archive: ['archive'] as const,
  imports: ['imports'] as const,
  import: (id: string) => ['import', id] as const,
};

/** Nach Schreibvorgängen: alles neu holen, was davon betroffen sein kann. */
function invalidateGraph(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [
    'gaps',
    'gapSummary',
    'persons',
    'person',
    'personAudit',
    'relationships',
    'relationship',
    'tree',
    'relationPath',
    'dashboard',
    // Die Bildseite rechnet den geschätzten Aufnahmezeitraum aus den
    // Lebensdaten der Personen darauf. Ein geändertes Geburtsjahr verschiebt
    // dieses Fenster – ohne diese Zeile bliebe am Bild die alte Schätzung
    // stehen, bis jemand die Seite neu lädt.
    'mediaItem',
    'media',
    'events',
    'event',
    'activity',
  ]) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

// ── Sitzung ────────────────────────────────────────────────────────────────

export function useSession(options?: Partial<UseQueryOptions<SessionResponse | null>>) {
  return useQuery<SessionResponse | null>({
    queryKey: keys.session,
    queryFn: async () => {
      try {
        return await api.get<SessionResponse>('/auth/me');
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
    ...options,
  });
}

/**
 * Darf die angemeldete Person Daten ändern?
 *
 * Eine Stelle statt zehnmal `role === 'admin' || role === 'editor'`: Leser
 * sollen nirgends ein Eingabefeld oder einen Knopf sehen, der am Ende mit 403
 * zurückkommt – und das lässt sich nur zentral zuverlässig halten.
 */
export function useCanWrite(): boolean {
  const { data: user } = useSession();
  return Boolean(user && WRITE_ROLES.includes(user.role));
}

/**
 * Ist die angemeldete Person Administrator?
 *
 * Gleiche Begründung wie bei `useCanWrite`: Wer nur Admins etwas zeigen darf –
 * etwa das Änderungsprotokoll – fragt hier und nicht selbst nach der Rolle.
 */
export function useIsAdmin(): boolean {
  const { data: user } = useSession();
  return user?.role === 'admin';
}

/**
 * Wer dieses Konto im Stammbaum *ist* – oder `null`, solange niemand das Konto
 * mit einer Person verknüpft hat.
 *
 * Eine Stelle statt überall `session.data?.person`: an dieser Person hängen
 * zwei Voreinstellungen, die die ganze Oberfläche durchziehen – die
 * Wurzelperson des Stammbaums und der erste Treffer jeder Personensuche.
 */
export function useMyPerson(): PersonRef | null {
  const { data: user } = useSession();
  return user?.person ?? null;
}

export function useAuthStatus() {
  return useQuery({
    queryKey: keys.authStatus,
    queryFn: () => api.get<{ needsSetup: boolean }>('/auth/status'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<SessionResponse>('/auth/login', input),
    onSuccess: (user) => {
      qc.setQueryData(keys.session, user);
      void qc.invalidateQueries();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/logout'),
    onSuccess: () => {
      qc.setQueryData(keys.session, null);
      qc.clear();
    },
  });
}

export function useSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; name: string; password: string }) =>
      api.post<SessionResponse>('/auth/setup', input),
    onSuccess: (user) => {
      qc.setQueryData(keys.session, user);
      void qc.invalidateQueries({ queryKey: keys.authStatus });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => api.patch<SessionResponse>('/auth/me', input),
    onSuccess: (user) => qc.setQueryData(keys.session, user),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: true }>('/auth/me/password', input),
  });
}

// ── Archiv ─────────────────────────────────────────────────────────────────

/**
 * Wie dieses Archiv heißt. Absichtlich nicht aus einem Nachnamen abgeleitet –
 * das würde eine Linie bevorzugen. `label` ist die persönliche Bezeichnung des
 * Betrachters und schlägt `name`.
 */
export function useArchive() {
  return useQuery({
    queryKey: keys.archive,
    queryFn: () => api.get<ArchiveInfo>('/archive'),
    staleTime: 10 * 60_000,
  });
}

export function useUpdateArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ArchiveInput) => api.patch<ArchiveInfo>('/archive', input),
    onSuccess: (info) => qc.setQueryData(keys.archive, info),
  });
}

export function useSetArchiveLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string | null) => api.put<ArchiveInfo>('/archive/label', { label }),
    onSuccess: (info) => qc.setQueryData(keys.archive, info),
  });
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard,
    queryFn: () => api.get<DashboardResponse>('/dashboard'),
    staleTime: 30_000,
  });
}

// ── Aktivitäten ────────────────────────────────────────────────────────────

/**
 * Das Änderungsprotokoll als Liste. Bearbeiter bekommen vom Server ohnehin
 * nur die eigenen Einträge – `userId` wirkt allein für Administratoren.
 */
export function useActivity(params: Partial<ActivityListQuery>) {
  return useQuery({
    queryKey: keys.activity(params),
    queryFn: () => api.get<Paginated<AuditEntry>>(`/activity${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });
}

// ── Personen ───────────────────────────────────────────────────────────────

export function usePersons(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.persons(params),
    queryFn: () => api.get<Paginated<PersonSummary>>(`/persons${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: keys.person(id ?? ''),
    queryFn: () => api.get<PersonDetail>(`/persons/${id}`),
    enabled: Boolean(id),
  });
}

/** Änderungsprotokoll einer Person – wer hat wann was geändert. */
export function usePersonAudit(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.personAudit(id ?? ''),
    queryFn: () => api.get<AuditEntry[]>(`/persons/${id}/audit`),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PersonInput) => api.post<PersonSummary>('/persons', input),
    onSuccess: () => invalidateGraph(qc),
  });
}

export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PersonPatch }) =>
      api.patch<PersonSummary>(`/persons/${id}`, input),
    onSuccess: () => invalidateGraph(qc),
  });
}

export function useDeletePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/persons/${id}`),
    onSuccess: () => invalidateGraph(qc),
  });
}

// ── Beziehungen ────────────────────────────────────────────────────────────

export function useRelationships(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.relationships(params),
    queryFn: () => api.get<Paginated<RelationshipSummary>>(`/relationships${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });
}

export function useRelationship(id: string | undefined) {
  return useQuery({
    queryKey: keys.relationship(id ?? ''),
    queryFn: () => api.get<RelationshipSummary>(`/relationships/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateRelationship() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RelationshipInput) => api.post<RelationshipSummary>('/relationships', input),
    onSuccess: () => invalidateGraph(qc),
  });
}

export function useUpdateRelationship() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<RelationshipInput> }) =>
      api.patch<RelationshipSummary>(`/relationships/${id}`, input),
    onSuccess: () => invalidateGraph(qc),
  });
}

export function useDeleteRelationship() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/relationships/${id}`),
    onSuccess: () => invalidateGraph(qc),
  });
}

// ── Ereignisse ─────────────────────────────────────────────────────────────

export function useEvents(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.events(params),
    queryFn: () => api.get<Paginated<EventSummary>>(`/events${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });
}

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: keys.event(id ?? ''),
    queryFn: () => api.get<EventSummary>(`/events/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EventInput) => api.post<EventSummary>('/events', input),
    onSuccess: () => invalidateGraph(qc),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<EventInput> }) =>
      api.patch<EventSummary>(`/events/${id}`, input),
    onSuccess: () => invalidateGraph(qc),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/events/${id}`),
    onSuccess: () => invalidateGraph(qc),
  });
}

// ── Orte ───────────────────────────────────────────────────────────────────

export function usePlaces(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.places(params),
    queryFn: () => api.get<Paginated<PlaceSummary>>(`/places${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });
}

export function usePlace(id: string | undefined) {
  return useQuery({
    queryKey: keys.place(id ?? ''),
    queryFn: () => api.get<PlaceSummary>(`/places/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreatePlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlaceInput) => api.post<PlaceSummary>('/places', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['places'] });
      void qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useUpdatePlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<PlaceInput> }) =>
      api.patch<PlaceSummary>(`/places/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['places'] });
      void qc.invalidateQueries({ queryKey: ['place'] });
    },
  });
}

export function useDeletePlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/places/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['places'] });
      invalidateGraph(qc);
    },
  });
}

// ── Medien ─────────────────────────────────────────────────────────────────

export function useMediaList(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.media(params),
    queryFn: () => api.get<Paginated<MediaSummary>>(`/media${toQueryString(params)}`),
    placeholderData: (prev) => prev,
    // Solange Ableitungen laufen, öfter nachsehen.
    refetchInterval: (query) => {
      const data = query.state.data as Paginated<MediaSummary> | undefined;
      return data?.items.some((m) => m.processingState === 'pending') ? 2500 : false;
    },
  });
}

export function useMediaItem(id: string | undefined) {
  return useQuery({
    queryKey: keys.mediaItem(id ?? ''),
    queryFn: () => api.get<MediaSummary>(`/media/${id}`),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data as MediaSummary | undefined;
      return data?.processingState === 'pending' ? 2000 : false;
    },
  });
}

/** Weiterhin unter dem alten Namen erreichbar – die Form kommt jetzt aus shared. */
export type UploadResult = MediaUploadResult;

/**
 * `sourceOnly`: die Datei ist das Belegstück einer Quelle und gehört nicht ins
 * Fotoalbum – der Weg über den Quellen-Dialog. Alles andere lädt normal hoch.
 */
export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ files, sourceOnly }: { files: File[]; sourceOnly?: boolean }) => {
      const formData = new FormData();
      for (const file of files) formData.append('files', file, file.name);
      return api.upload<MediaUploadResult>(
        sourceOnly ? '/media?sourceOnly=1' : '/media',
        formData,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

/**
 * Ein zwischengelagerter Upload wird angelegt („trotzdem hochladen") oder
 * verworfen. Beides erst nach der Antwort im Dialog – bis dahin liegt die Datei
 * auf dem Server, ist aber kein Medium.
 */
export function useResolveStagedUpload() {
  const qc = useQueryClient();
  return useMutation({
    /** Beim Bestätigen das neue Medium, beim Verwerfen nichts. */
    mutationFn: async ({
      stagingId,
      keep,
    }: {
      stagingId: string;
      keep: boolean;
    }): Promise<MediaSummary | null> => {
      if (!keep) {
        await api.del<{ ok: true }>(`/media/staged/${stagingId}`);
        return null;
      }
      return api.post<MediaSummary>(`/media/staged/${stagingId}/confirm`);
    },
    onSuccess: (_data, variables) => {
      if (!variables.keep) return;
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useUpdateMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MediaPatch }) =>
      api.patch<MediaSummary>(`/media/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: ['mediaItem'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

/**
 * Den geschätzten Aufnahmezeitraum als Aufnahmedatum übernehmen.
 *
 * Ohne Nutzlast: der Server rechnet die Schätzung selbst noch einmal und
 * schreibt, was *jetzt* gilt. Damit kann nicht die Zahl gespeichert werden,
 * die hier vor zehn Minuten auf dem Bildschirm stand – und der Knopf bleibt
 * auch dann richtig, wenn inzwischen jemand anders ein Geburtsjahr nachgetragen
 * hat.
 */
export function useApplyDateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<MediaSummary>(`/media/${id}/apply-date-estimate`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: ['mediaItem'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * „Auf welchem der beiden Bilder ist sie jünger?" – die nächsten Fragen.
 *
 * Ohne `mediaId` archivweit, für die Übersicht; mit `mediaId` zu genau einem
 * Bild, für die Zeitraum-Karte.
 */
export function useAgeComparisons(params: { mediaId?: string; limit?: number } = {}) {
  const canWrite = useCanWrite();
  return useQuery({
    queryKey: keys.ageComparisons(params),
    queryFn: () => api.get<AgeComparisonTasks>(`/photo-dates/comparisons${toQueryString(params)}`),
    // Leser bekommen keine Frage vorgelegt, deren Antwort verworfen würde.
    enabled: canWrite,
    staleTime: 30_000,
  });
}

export function useAnswerAgeComparison() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AgeComparisonInput) =>
      api.post<{ ok: true }>('/photo-dates/comparisons', input),
    onSuccess: () => {
      // Die Antwort verschiebt den geschätzten Zeitraum des Bildes – und damit
      // auch, welche Frage als nächste lohnt.
      for (const key of ['ageComparisons', 'mediaItem', 'media', 'dashboard']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

/** Eine Vergleichsantwort zurücknehmen – der Ausgang aus einem Fehlklick. */
export function useDeleteAgeComparison() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/photo-dates/comparisons/${id}`),
    onSuccess: () => {
      for (const key of ['ageComparisons', 'mediaItem', 'media', 'dashboard']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/media/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: ['mediaItem'] });
      invalidateGraph(qc);
    },
  });
}

// ── Bildmarkierungen ───────────────────────────────────────────────────────

/** Nach jeder Änderung an Markierungen: Bild, Galerie und Personen neu holen. */
function invalidateRegions(qc: ReturnType<typeof useQueryClient>) {
  // Die Vorschläge hängen an den Markierungen: wer zuordnet oder eine
  // Zuordnung zurücknimmt, verändert damit die Vergleichsgrundlage.
  void qc.invalidateQueries({ queryKey: ['faceSuggestions'] });
  void qc.invalidateQueries({ queryKey: ['personFaceCandidates'] });
  void qc.invalidateQueries({ queryKey: ['archiveFaceCandidates'] });
  void qc.invalidateQueries({ queryKey: ['gaps'] });
  void qc.invalidateQueries({ queryKey: ['gapSummary'] });
  for (const key of ['mediaItem', 'media', 'person', 'dashboard']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useCreateRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, input }: { mediaId: string; input: MediaRegionInput }) =>
      api.post<MediaRegion>(`/media/${mediaId}/regions`, input),
    onSuccess: () => invalidateRegions(qc),
  });
}

/**
 * Ein ganzer Erkennungslauf auf einmal – leere Rahmen, ohne Zuordnung.
 *
 * Ein Aufruf statt n: bei zwölf Gesichtern wären zwölf einzelne Anfragen
 * zwölf Runden über das Netz und zwölf Protokolleinträge.
 */
export function useDetectRegions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, input }: { mediaId: string; input: DetectedRegionsInput }) =>
      api.post<MediaRegion[]>(`/media/${mediaId}/regions/detected`, input),
    onSuccess: () => invalidateRegions(qc),
  });
}

/**
 * Markierungen eines Bildes vermessen – gerechnet im Browser, gespeichert über
 * die API.
 *
 * Ausdrücklich als Mutation und nicht als nackter `api.post`: nur so hängt das
 * Auffrischen der Vorschläge fest daran. Vorher rief die Bilddetailansicht
 * `embedRegions` direkt auf, und niemand sagte den Vorschlägen, dass es jetzt
 * etwas zu holen gibt – sie erschienen erst, wenn irgendeine andere Änderung
 * zufällig eine Invalidierung auslöste.
 */
export function useEmbedRegions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      image,
      regions,
    }: {
      image: HTMLImageElement;
      regions: Array<{ id: string; x: number; y: number; width: number; height: number }>;
    }) => embedRegions(image, regions),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['faceSuggestions'] });
      void qc.invalidateQueries({ queryKey: ['personFaceCandidates'] });
  void qc.invalidateQueries({ queryKey: ['archiveFaceCandidates'] });
    },
  });
}

/**
 * Vorschläge für die offenen Gesichter eines Bildes – für die Detailansicht
 * neben dem Bild.
 */
export function useFaceSuggestions(mediaId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['faceSuggestions', mediaId],
    queryFn: () =>
      api.get<{
        clusters: FaceCluster[];
        pending: number;
        /** Markierungen dieses Bildes ohne aktuelle Merkmalsreihe. */
        unmeasured: Array<{ id: string; x: number; y: number; width: number; height: number }>;
        /** Zurückgestellte Gesichter dieses Bildes. */
        deferred: number;
      }>(`/faces/suggestions/${mediaId}`),
    enabled: Boolean(mediaId) && enabled,
  });
}

/**
 * Weitere mögliche Bilder einer Person – die Umkehrung der Vorschläge.
 *
 * Eigener Schlüssel und nicht Teil von `person`: die Antwort hängt am ganzen
 * Archiv und nicht an dieser einen Person, und sie soll nach jeder Zuordnung
 * neu geholt werden – auch nach einer, die auf einer Bildseite passiert ist.
 */
export function usePersonFaceCandidates(personId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['personFaceCandidates', personId],
    queryFn: () => api.get<PersonFaceCandidates>(`/faces/for-person/${personId}`),
    enabled: Boolean(personId) && enabled,
  });
}

/**
 * „Wer ist dieses Gesicht?" – die stärksten Vorschläge im ganzen Archiv.
 *
 * Für die Übersicht: dort gibt es weder ein Bild noch eine Person, von der aus
 * gefragt werden könnte. Derselbe Schlüsselraum wie die anderen beiden
 * Richtungen, damit jede Zuordnung – egal wo sie passiert – auch diese Liste
 * erneuert.
 */
export function useArchiveFaceCandidates(limit = 4, enabled = true) {
  return useQuery({
    queryKey: ['archiveFaceCandidates', limit],
    queryFn: () => api.get<ArchiveFaceCandidates>(`/faces/next?limit=${limit}`),
    enabled,
  });
}

/** Zurückgestellte Gesichter eines Bildes wieder vorlegen. */
export function useResetDeferredFaces() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) =>
      api.post<{ restored: number }>(`/faces/deferred/reset/${mediaId}`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['faceSuggestions'] }),
  });
}

/** Eine ganze Gruppe einer Person zuordnen. */
/**
 * Die Antwort auf „ist das Person XY?" – ja, nein oder weiß ich nicht.
 *
 * Ein Aufruf für alle drei, weil es für den Benutzer eine Handlung ist: er
 * beantwortet eine Frage. „Ja" trägt die Person ein, „nein" merkt sich, dass es
 * diese nicht ist, „weiß ich nicht" stellt das Gesicht zurück.
 */
export function useFaceVerdict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      regionIds: string[];
      answer: 'yes' | 'no' | 'unknown';
      personId?: string | null;
      similarity?: number | null;
    }) =>
      api.post<{ answer: string; assigned?: number; recorded?: number }>('/faces/verdict', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['faceSuggestions'] });
      invalidateRegions(qc);
    },
  });
}

export function useUpdateRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ regionId, input }: { regionId: string; input: MediaRegionPatch }) =>
      api.patch<MediaRegion>(`/media/regions/${regionId}`, input),
    onSuccess: () => invalidateRegions(qc),
  });
}

export function useDeleteRegion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      regionId,
      keepLink,
      notAFace,
    }: {
      regionId: string;
      keepLink?: boolean;
      /**
       * „Hier ist kein Gesicht." Die Stelle wird gemerkt, damit die Erkennung
       * sie nicht wieder setzt – und die Sicherheit des Fehlfunds bleibt für
       * die Kalibrierung erhalten.
       */
      notAFace?: boolean;
    }) =>
      api.del<{ ok: true; linkRemoved: boolean; remembered: boolean }>(
        `/media/regions/${regionId}` +
          (keepLink || notAFace
            ? `?${[keepLink ? 'keepLink=1' : '', notAFace ? 'notAFace=1' : ''].filter(Boolean).join('&')}`
            : ''),
      ),
    onSuccess: () => invalidateRegions(qc),
  });
}

export function useSetAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, personId }: { mediaId: string; personId: string }) =>
      api.post<{ ok: true }>(`/media/${mediaId}/set-avatar`, { personId }),
    onSuccess: () => invalidateGraph(qc),
  });
}

/**
 * Alle noch unkomprimierten Bilder nachträglich verkleinern lassen. Nur für
 * Administratoren – es schreibt jede Originaldatei neu.
 */
export function useReprocessImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ queued: number }>('/media/reprocess-images'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: ['mediaItem'] });
    },
  });
}

export function useReprocessMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`/media/${id}/reprocess`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
      void qc.invalidateQueries({ queryKey: ['mediaItem'] });
    },
  });
}

// ── Quellen ────────────────────────────────────────────────────────────────

export function useSources(params: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.sources(params),
    queryFn: () => api.get<Paginated<SourceSummary>>(`/sources${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });
}

/** Eine einzelne Quelle – für die Detailansicht, die von mehreren Seiten aus aufgeht. */
export function useSource(id: string | undefined) {
  return useQuery({
    queryKey: keys.source(id ?? ''),
    queryFn: () => api.get<SourceSummary>(`/sources/${id}`),
    enabled: Boolean(id),
  });
}

export function useSourceLinks(id: string | undefined) {
  return useQuery({
    queryKey: keys.sourceLinks(id ?? ''),
    queryFn: () => api.get<SourceLinkSummary[]>(`/sources/${id}/links`),
    enabled: Boolean(id),
  });
}

export function useCreateSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SourceInput) => api.post<SourceSummary>('/sources', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
      void qc.invalidateQueries({ queryKey: keys.dashboard });
    },
  });
}

export function useUpdateSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<SourceInput> }) =>
      api.patch<SourceSummary>(`/sources/${id}`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sources'] }),
  });
}

export function useDeleteSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/sources/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

export function useLinkSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      entityType,
      entityId,
      page,
    }: {
      sourceId: string;
      entityType: 'person' | 'relationship' | 'event' | 'media';
      entityId: string;
      page?: string | null;
    }) => api.post<{ ok: true }>(`/sources/${sourceId}/links`, { entityType, entityId, page: page ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
      void qc.invalidateQueries({ queryKey: ['sourceLinks'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

export function useUnlinkSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => api.del<{ ok: true }>(`/sources/links/${linkId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
      void qc.invalidateQueries({ queryKey: ['sourceLinks'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

// ── Graph ──────────────────────────────────────────────────────────────────

export function useTree(params: {
  rootId: string | undefined;
  /**
   * Ohne Angabe zeigt der Server die ganze Linie – so weit, wie erfasst ist.
   * Die Oberfläche schickt hier vorerst nichts.
   */
  ancestorDepth?: number;
  descendantDepth?: number;
  includePartners: boolean;
  /**
   * Personen, die im Ausschnitt stehen müssen, auch wenn die Wurzel sie nicht
   * enthält – der hervorgehobene Verwandtschaftsweg. Sortiert in den
   * Abfrageschlüssel, damit dieselbe Menge in anderer Reihenfolge nicht als
   * neue Abfrage zählt.
   */
  includeIds?: string[];
}) {
  const includeIds = params.includeIds?.length ? [...params.includeIds].sort() : undefined;
  return useQuery({
    queryKey: keys.tree({ ...params, includeIds }),
    queryFn: () =>
      api.get<TreeResponse>(
        `/tree${toQueryString({
          rootId: params.rootId,
          ancestorDepth: params.ancestorDepth,
          descendantDepth: params.descendantDepth,
          includePartners: params.includePartners,
          includeIds: includeIds?.join(','),
        })}`,
      ),
    placeholderData: (prev) => prev,
  });
}

export function useRelationPath(fromId: string | undefined, toId: string | undefined) {
  return useQuery({
    queryKey: keys.relationPath(fromId ?? '', toId ?? ''),
    queryFn: () => api.get<RelationPathResponse>(`/tree/path${toQueryString({ fromId, toId })}`),
    enabled: Boolean(fromId && toId && fromId !== toId),
  });
}

// ── Benutzer ───────────────────────────────────────────────────────────────

/** Nur für Administratoren – `enabled: false` spart allen anderen die 403. */
/**
 * Was ein Konto sähe, wenn der Schutz von Bild- und Personendaten scharf wäre.
 *
 * Gerechnet wird serverseitig immer mit eingeschalteter Durchsetzung, auch
 * solange der Archivschalter aus ist – sonst könnte man vor dem Umlegen nicht
 * nachsehen, was er anrichtet.
 */
export function useScopePreview(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['scope-preview', userId] as const,
    queryFn: () => api.get<ScopePreview>(`/users/${userId}/scope-preview?limit=8`),
    enabled: Boolean(userId) && enabled,
    staleTime: 30_000,
  });
}

/**
 * Zugriff auf einen verborgenen Zweig anfragen.
 *
 * Benannt wird die **Spitzenperson**, nicht der einzelne Platzhalter: sonst
 * entstünde eine Freigabeverwaltung aus tausend Einzelfällen. Wer anfragt,
 * erfährt dabei nichts – der Name steht erst beim Administrator.
 */
/**
 * Gibt es diesen Namen schon außerhalb des eigenen Bereichs?
 *
 * Der ehrliche Preis des Schreibbereichs: wer einen Zweig nicht sieht, legt
 * jemanden neu an, den es dort längst gibt. Die Antwort ist bewusst ein
 * nacktes Ja/Nein – dass es einen Treffer gibt, hilft; wer er ist, geht den
 * Anlegenden nichts an.
 */
export function useDuplicateOutsideScope(givenNames: string, surname: string, enabled: boolean) {
  const name = givenNames.trim();
  return useQuery({
    queryKey: ['duplicate-outside', name, surname.trim()] as const,
    queryFn: () =>
      api.get<{ outsideScope: boolean }>(
        `/persons/check-duplicate${toQueryString({ givenNames: name, surname: surname.trim() })}`,
      ),
    enabled: enabled && name.length >= 2,
    staleTime: 60_000,
  });
}

export function useBranches(enabled = true) {
  return useQuery({
    queryKey: ['branches'] as const,
    queryFn: () => api.get<BranchSummary[]>('/branches'),
    enabled,
  });
}

export function useBranchSuggestions(enabled = true) {
  return useQuery({
    queryKey: ['branch-suggestions'] as const,
    queryFn: () => api.get<BranchSuggestion[]>('/branches/suggestions'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useAccessRequests(enabled = true) {
  return useQuery({
    queryKey: ['access-requests'] as const,
    queryFn: () => api.get<AccessRequestSummary[]>('/branches/requests'),
    enabled,
  });
}

/** Nach jeder Änderung an Zweigen ändert sich, wer was sieht – also alles neu. */
function invalidateScope(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['branches'] });
  void qc.invalidateQueries({ queryKey: ['access-requests'] });
  // Präfixe, nicht `keys.tree(params)`: der Bereich ändert sich für alle
  // Parameterkombinationen zugleich.
  void qc.invalidateQueries({ queryKey: ['tree'] });
  void qc.invalidateQueries({ queryKey: ['persons'] });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BranchInput) => api.post<BranchSummary>('/branches', input),
    onSuccess: () => invalidateScope(qc),
  });
}

export function useDeleteBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/branches/${id}`),
    onSuccess: () => invalidateScope(qc),
  });
}

export function useSetBranchGrants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>
      api.put<BranchSummary>(`/branches/${id}/grants`, { userIds }),
    onSuccess: () => invalidateScope(qc),
  });
}

export function useDecideAccessRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, grant, note }: { id: string; grant: boolean; note?: string | null }) =>
      api.post<AccessRequestSummary[]>(`/branches/requests/${id}/decide`, {
        grant,
        note: note ?? null,
        branchId: null,
      }),
    onSuccess: () => invalidateScope(qc),
  });
}

export function useRequestAccess() {
  return useMutation({
    mutationFn: (input: { kind: 'branch' | 'media'; apexPersonId?: string; mediaId?: string; message?: string }) =>
      api.post<{ ok: true }>('/branches/requests', input),
  });
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: keys.users,
    queryFn: () => api.get<UserSummary[]>('/users'),
    enabled,
  });
}

/**
 * Mitglieder des Archivs – für jede angemeldete Person sichtbar.
 *
 * Mit `personId`/`mediaId` kommt die Liste danach sortiert, wer dem Thema der
 * Frage am nächsten steht, und sagt in `suggested`, wen sie vorschlägt.
 */
export function useFamilyMembers(
  enabled = true,
  params: { personId?: string; mediaId?: string } = {},
) {
  const key = { personId: params.personId ?? null, mediaId: params.mediaId ?? null };
  return useQuery({
    queryKey: keys.familyMembers(key),
    queryFn: () => api.get<FamilyMember[]>(`/users/family${toQueryString(params)}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      api.patch<UserSummary>(`/users/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.users });
      // Die Zuordnung zu einer Person ändert die Vorschläge bei „Familie fragen“
      // – und, wenn es das eigene Konto war, „ich selbst" in der ganzen
      // Oberfläche: Wurzelperson des Stammbaums und erste Wahl jeder Suche.
      void qc.invalidateQueries({ queryKey: ['familyMembers'] });
      void qc.invalidateQueries({ queryKey: keys.session });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/users/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post<{ ok: true }>(`/users/${id}/password`, { password }),
  });
}

export function useInvites() {
  return useQuery({ queryKey: keys.invites, queryFn: () => api.get<InviteSummary[]>('/users/invites') });
}

export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      role: 'admin' | 'editor' | 'viewer';
      personId?: string | null;
    }) => api.post<InviteSummary>('/users/invites', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.invites }),
  });
}

export function useDeleteInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/users/invites/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.invites }),
  });
}

export function useCheckInvite(token: string | undefined) {
  return useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<{ email: string; role: string }>(`/users/invites/check/${token}`),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; name: string; password: string }) =>
      api.post<UserSummary>('/users/invites/accept', input),
    onSuccess: () => void qc.invalidateQueries(),
  });
}

// ── Suche ──────────────────────────────────────────────────────────────────

export function useSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => api.get<SearchResponse>(`/search${toQueryString({ q, limit: 6 })}`),
    enabled: enabled && q.trim().length >= 2,
    staleTime: 20_000,
  });
}

// ── Familienfragen ─────────────────────────────────────────────────────────

/** Nach jeder Änderung an Fragen: Listen, Detail und Zähler neu holen. */
function invalidateQuestions(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['questions', 'question', 'questionCounts']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useQuestions(params: {
  box?: 'inbox' | 'review' | 'mine' | 'all';
  status?: string;
  personId?: string;
  mediaId?: string;
  page?: number;
}) {
  return useQuery({
    queryKey: keys.questions(params),
    queryFn: () =>
      api.get<Paginated<QuestionSummary>>(`/questions${toQueryString(params)}`),
    staleTime: 15_000,
  });
}

export function useQuestion(id: string | undefined) {
  return useQuery({
    queryKey: keys.question(id ?? ''),
    queryFn: () => api.get<QuestionDetail>(`/questions/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Zähler für die Navigation. Wird oft geholt, deshalb kurz zwischengespeichert
 * und ohne Fensterfokus-Nachladen.
 */
export function useQuestionCounts() {
  return useQuery({
    queryKey: keys.questionCounts,
    queryFn: () => api.get<QuestionCounts>('/questions/counts'),
    staleTime: 60_000,
  });
}

export function useCreateQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuestionInput) => api.post<QuestionDetail>('/questions', input),
    onSuccess: () => invalidateQuestions(qc),
  });
}

/**
 * „Habe ich gesehen“ – setzt den Lesestand einer Frage.
 *
 * Für Empfänger heißt das: die Frage ist gelesen. Für die Fragestellerin: die
 * Antworten sind gelesen, der Hinweis „neue Antwort“ verschwindet. Erst *nach*
 * dem Erfolg werden die Zähler neu geholt, sonst käme der alte Stand zurück.
 */
export function useMarkQuestionSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`/questions/${id}/seen`, {}),
    onSuccess: () => invalidateQuestions(qc),
  });
}

export function useAnswerQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, input }: { questionId: string; input: AnswerInput }) =>
      api.post<QuestionDetail>(`/questions/${questionId}/answers`, input),
    onSuccess: () => invalidateQuestions(qc),
  });
}

/**
 * Freigabe oder Ablehnung eines Vorschlags. Bei „übernehmen“ ändern sich auch
 * Personen- und Medien­daten – deshalb wird der halbe Cache ungültig.
 */
export function useDecideAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      answerId,
      accept,
      note,
    }: {
      answerId: string;
      accept: boolean;
      note?: string | null;
    }) =>
      api.post<QuestionDetail>(`/questions/answers/${answerId}/${accept ? 'accept' : 'reject'}`, {
        note: note ?? null,
      }),
    onSuccess: (_data, variables) => {
      invalidateQuestions(qc);
      if (variables.accept) {
        invalidateGraph(qc);
        invalidateRegions(qc);
        void qc.invalidateQueries({ queryKey: ['sources'] });
      }
    },
  });
}

export function useCloseQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<QuestionDetail>(`/questions/${id}/close`, {}),
    onSuccess: () => invalidateQuestions(qc),
  });
}

export function useDeleteQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/questions/${id}`),
    onSuccess: () => invalidateQuestions(qc),
  });
}

// ── Lücken / Aufgaben ──────────────────────────────────────────────────────

export function useGaps(params: { kind?: string; page?: number; pageSize?: number }) {
  return useQuery({
    queryKey: keys.gaps(params),
    queryFn: () => api.get<Paginated<GapItem>>(`/gaps${toQueryString(params)}`),
    staleTime: 15_000,
  });
}

export function useGapSummary() {
  return useQuery({
    queryKey: keys.gapSummary,
    queryFn: () => api.get<GapSummary>('/gaps/summary'),
    staleTime: 30_000,
  });
}

/**
 * Eine Lücke beiseitelegen („bleibt offen“). Danach ist sowohl die Liste als
 * auch der Fortschritt neu zu holen.
 */
export function useDismissGap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: string; entityId: string; note?: string | null }) =>
      api.post<{ ok: true }>('/gaps/dismiss', { ...input, note: input.note ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gaps'] });
      void qc.invalidateQueries({ queryKey: ['gapSummary'] });
    },
  });
}

// ── Import aus fremden Stammbäumen ─────────────────────────────────────────

export function useImports() {
  return useQuery({
    queryKey: keys.imports,
    queryFn: () => api.get<{ items: ImportSummary[] }>('/imports'),
    staleTime: 15_000,
  });
}

export function useImport(id: string | undefined) {
  return useQuery({
    queryKey: keys.import(id ?? ''),
    queryFn: () => api.get<ImportDetail>(`/imports/${id}`),
    enabled: Boolean(id),
    /**
     * Die Prüfmappe wird beim Entscheiden ständig verändert – aber immer nur
     * von der Person, die gerade davorsitzt. Nachladen im Hintergrund würde
     * hier nur die Auswahl unter den Fingern verschieben.
     */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useCreateImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<ImportSummary>('/imports', form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.imports });
    },
  });
}

type DecisionScope = 'personen' | 'verbindungen' | 'orte';

/**
 * Eine einzelne Entscheidung. Die Antwort ersetzt genau die eine Zeile im
 * zwischengespeicherten Detail – die ganze Mappe neu zu holen würde bei
 * zweihundert Personen bei jedem Klick ein paar hundert Kilobyte kosten und
 * die Liste unter dem Mauszeiger neu sortieren.
 */
export function useImportDecision(importId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scope, rowId, patch }: { scope: DecisionScope; rowId: string; patch: ImportDecisionPatch }) =>
      api.patch<ImportPersonItem | ImportEdgeItem | ImportPlaceItem>(
        `/imports/${importId}/${scope}/${rowId}`,
        patch,
      ),
    onSuccess: (updated, variables) => {
      qc.setQueryData<ImportDetail>(keys.import(importId), (old) => {
        if (!old) return old;
        const key = variables.scope === 'personen' ? 'persons' : variables.scope === 'verbindungen' ? 'edges' : 'places';
        const list = (old[key] as Array<{ id: string }>).map((row) =>
          row.id === variables.rowId ? (updated as { id: string }) : row,
        );
        return { ...old, [key]: list, plan: recomputePlan({ ...old, [key]: list } as ImportDetail) };
      });
    },
  });
}

export function useImportBulk(importId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportBulk) =>
      api.post<{ changed: number; detail: ImportDetail }>(`/imports/${importId}/sammelentscheidung`, input),
    onSuccess: (result) => {
      qc.setQueryData(keys.import(importId), result.detail);
    },
  });
}

export function useApplyImport(importId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportApplyInput) =>
      api.post<{
        personsCreated: number;
        personsUpdated: number;
        edgesCreated: number;
        placesCreated: number;
        refused: string[];
        sourceId: string | null;
      }>(`/imports/${importId}/uebernehmen`, input),
    onSuccess: () => {
      // Nach einer Übernahme stimmt fast nichts mehr, was zwischengespeichert war.
      void qc.invalidateQueries();
    },
  });
}

export function useDiscardImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean }>(`/imports/${id}/verwerfen`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.imports });
    },
  });
}

/**
 * Die Bilanz nach einer einzelnen Entscheidung neu rechnen.
 *
 * Dieselbe Rechnung steht im Server (`planOf`) – dort verbindlich, hier nur
 * für die Anzeige. Sie hier zu wiederholen kostet ein paar Zeilen und spart
 * bei jedem Klick eine Anfrage; und weil der Server vor dem Übernehmen erneut
 * rechnet, kann eine Abweichung nichts anrichten.
 */
function recomputePlan(detail: ImportDetail): ImportDetail['plan'] {
  let fieldsChanged = 0;
  for (const person of detail.persons) {
    if (person.decision !== 'update') continue;
    fieldsChanged += Object.values(person.fieldChoices).filter((c) => c.mode !== 'keep').length;
  }
  return {
    personsCreated: detail.persons.filter((p) => p.decision === 'create').length,
    personsUpdated: detail.persons.filter((p) => p.decision === 'update').length,
    fieldsChanged,
    edgesCreated: detail.edges.filter((e) => e.decision === 'create').length,
    edgesUpdated: detail.edges.filter((e) => e.decision === 'update').length,
    placesCreated: detail.places.filter((p) => p.decision === 'create').length,
    skipped:
      detail.persons.filter((p) => p.decision === 'skip').length +
      detail.edges.filter((e) => e.decision === 'skip').length,
    unresolved:
      detail.persons.filter((p) => p.unresolved).length +
      detail.edges.filter((e) => e.unresolved).length +
      detail.places.filter((p) => p.unresolved).length,
    blocked: detail.edges.filter((e) => e.blockers.length > 0).length,
  };
}
